import Portal from '@mui/material/Portal';
import { getGA4SessionAndClient } from '@niarab2c/frontend-commons/src/util/analyticsEvents';
import { getStorefrontUrl } from '@niarab2c/frontend-commons/src/util/storefrontUtils';
import CriteriaComponent from '@niarab2c/niarab2c-core/src/components/CriteriaComponent';
import { generateQueryString } from '@niarab2c/niarab2c-core/src/util/hotels';
import type { Storefront } from '@niarab2c/otabuilder-crudmodel/src';
import { diffInDays } from '@niaratech/niara-js-commons';
import { HotelCriteriaSkeleton } from '@niaratech/ota-components/src/HotelSearch/Criteria';
import DefaultPortalContext from '@niaratech/ota-components/src/portal/DefaultPortalContext';
import queryString from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import ThemeProvider from '../ThemeProvider';
import { type CriteriaForm } from '../app';
import { Dispatch, StorefrontConfig, createStore } from '../app/widget';
import { fetchStorefront } from '../util/loadStorefront';
import StyleSheet from './StyleSheet';
import WidgetPortalContainer from './WidgetPortalContainer';
import * as S from './styles';
type Props = {
  storefrontId: string;
  headerSize?: string;
  orientation: 'VERTICAL' | 'HORIZONTAL';
  enableLongStayDudaStyle?: boolean;
  ga4MeasurementId?: string;
};
const Widget: React.FC<Props> = ({
  storefrontId,
  headerSize,
  orientation,
  enableLongStayDudaStyle = false,
  ga4MeasurementId: _ga4MeasurementId
}) => {
  const [init, setInit] = useState(undefined);
  const [store] = useState(() => createStore());
  const dispatch = store.dispatch as Dispatch;
  const [storefront, setStorefront] = useState<Storefront>();
  const ga4MeasurementId = storefront?.googleAnalyticsId ?? _ga4MeasurementId;
  useEffect(() => {
    setInit(null);
    fetchStorefront(storefrontId).then(storefront => {
      setStorefront(storefront);
      if (storefront) {
        dispatch(StorefrontConfig.initStorefront(storefront)).then(() => setInit(true));
      } else {
        // não foi possível carregar o storefront
        setInit(false);
      }
    });
  }, [dispatch, storefrontId]);
  const ga4ClientAndSessionRef = React.useRef<Awaited<ReturnType<typeof getGA4SessionAndClient>>>();
  React.useEffect(() => {
    getGA4SessionAndClient(ga4MeasurementId).then(value => ga4ClientAndSessionRef.current = value);
  }, [ga4MeasurementId]);
  const onSearch = useCallback((criteria: CriteriaForm) => {
    const longStay_enabled = storefront?.longStay_enabled;
    const nights = diffInDays(criteria?.endDate, criteria?.startDate);
    const queryStringCriteria = `${ga4ClientAndSessionRef.current ? queryString.stringify(ga4ClientAndSessionRef.current) : ''}${generateQueryString(criteria) ?? ''}`;
    const hotelId = criteria.destinations?.hotelIds?.length === 1 ? criteria.destinations?.hotelIds[0] : '';
    let redirectTo = '';
    if (!longStay_enabled) {
      redirectTo = `/hotels/${hotelId}#${queryStringCriteria}`;
    } else if (nights < 30) {
      redirectTo = `/hotels/${hotelId}#${queryStringCriteria}`;
    } else {
      redirectTo = `/longStay/${hotelId}#${queryStringCriteria}`;
    }

    // verifica se tem o niaraAuthToken gerado pelo WidgetLogin que armazena globalmente no window['NIARA']
    const niaraAuthToken = window?.['NIARA']?.getToken();
    if (niaraAuthToken) {
      return window.open(`${getStorefrontUrl(storefront)}/auth/niaraAuth#${queryString.stringify({
        access_token: niaraAuthToken,
        redirect_to: redirectTo
      })}`);
    }
    return window.open(`${getStorefrontUrl(storefront)}${redirectTo}`);
  }, [storefront]);
  const [defaultPortalContextValue, setDefaultPortalContextValue] = useState();
  if (init === false) return <>Erro de configuração</>;
  if (!init) return <HotelCriteriaSkeleton isLongStay={storefront?.longStay_enabled} searchEngine_orientation={orientation} />;
  return <Provider store={store}>
      <StyleSheet>
        <ThemeProvider>
          <div className="otabuilder-widget-container">
            <S.Container>
              <S.CenteredContainer>
                <DefaultPortalContext.Provider value={defaultPortalContextValue}>
                  <CriteriaComponent isPersonSearch={false} headerSize={headerSize} onHotelSearch={onSearch} isLongStay={storefront?.longStay_enabled} forceHideCart={true} enableLongStayDudaStyle={enableLongStayDudaStyle} />
                </DefaultPortalContext.Provider>
              </S.CenteredContainer>
            </S.Container>
          </div>
        </ThemeProvider>
        <Portal>
          <WidgetPortalContainer setDefaultPortalContext={setDefaultPortalContextValue} />
        </Portal>
      </StyleSheet>
    </Provider>;
};
export default Widget;